import { FC, useCallback, useMemo } from "react";
import classNames from "classnames";
import { MultiValue, SingleValue } from "react-select";
import { useNavigate } from "react-router";

import { useAppDispatch, useAppSelector } from "../../hooks/useRedux";
import { setSelectedBrandId, setViewBrandId, updateSelectedBrandId } from "../../store/brands/slice";
import { selectBrandLoading, selectListBrandsState, selectSelectedBrandIdState } from "../../store/selectors/brands";
import { selectBrandEveryDay7dSummaryForBrand } from "../../store/selectors/scores";
import SelectCarma from "../Select/Select";
import { ISelectCarmaOption, ISelectCarmaOptionType } from "../Select/types";
import Skeleton from "../Skeleton/Skeleton";
import { createOptionsFromBrands } from "../../utils/createOptionsFromBrands";
import { MainBrandSelectProps } from "./types";
import { PlusIcon } from "../icons/PlusIcon";
import { ERoutes } from "../../constants/routes";
import { isSingleSelectOption } from "../Select/utils/isSingleSelectOption";

import styles from "./MainBrandSelect.module.css";

const MainBrandSelect: FC<MainBrandSelectProps> = ({
  className,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const storeBrands = useAppSelector(selectListBrandsState);
  const storeSelectedBrandId = useAppSelector(selectSelectedBrandIdState);
  const storeBrand7dSummary = useAppSelector(selectBrandEveryDay7dSummaryForBrand);
  const storeIsLoading = useAppSelector(selectBrandLoading);

  const newBrandOption: ISelectCarmaOption = useMemo(() => ({
    id: -1,
    value: -1,
    type: ISelectCarmaOptionType.Button,
    label: "New Brand",
    icon: <PlusIcon />,
    onClick: () => navigate(ERoutes.AddNewBrand),
  }), [navigate]);

  const options = useMemo(
    () => [...createOptionsFromBrands(storeBrands, storeBrand7dSummary ?? undefined), newBrandOption],
    [storeBrands, storeBrand7dSummary, newBrandOption]
  );

  const selectedOption = useMemo(() => {
    if (!options.length || storeSelectedBrandId == null) return undefined;
    return options.find((o) => o.id === storeSelectedBrandId);
  }, [options, storeSelectedBrandId]);

  const handleChange = useCallback(
    (newValue: SingleValue<ISelectCarmaOption> | MultiValue<ISelectCarmaOption>) => {
      if (!isSingleSelectOption(newValue)) return;
      if (newValue.id < 0) return;
      dispatch(setSelectedBrandId(newValue.id));
      dispatch(setViewBrandId(newValue.id));
      dispatch(updateSelectedBrandId(newValue.id));
      navigate(ERoutes.Brands);
    },
    [dispatch, navigate]
  );

  if (storeIsLoading && !storeBrands) {
    return <Skeleton className={className} height={48} />;
  }

  return (
    <div className={classNames(styles["main-brand-select"], className)}>
      <SelectCarma value={selectedOption} options={options} onChange={handleChange} />
    </div>
  );
};

export default MainBrandSelect;

